"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import { BAG_DISCOUNT_PERCENT, BagTotals, formatUsd } from "@/lib/comix/bagConfig";

function AnimatedSavings({ amount }: { amount: number }) {
  const controls = useAnimation();
  const prevAmount = useRef(amount);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    if (amount > prevAmount.current) {
      controls.start({
        scale: [1, 1.2, 0.93, 1.06, 1],
        y: [0, -7, 2, -2, 0],
        color: ["#F52F81", "#ff0066", "#F52F81"],
        transition: { duration: 0.45, ease: "easeOut" },
      });
    }
    prevAmount.current = amount;
  }, [amount, controls]);

  return (
    <motion.span animate={controls} style={{ display: "inline-block" }}>
      {formatUsd(amount)}!
    </motion.span>
  );
}

function DiscountBadge() {
  return (
    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <defs>
          <linearGradient id="discountBadgeGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F52F81" />
            <stop offset="100%" stopColor="#FD7468" />
          </linearGradient>
        </defs>
        <path
          d="M12 1.5l2.4 2.1 3.1-.6 1 3 3 1-.6 3.1L23 12l-2.1 2.4.6 3.1-3 1-1 3-3.1-.6L12 22.5l-2.4-2.1-3.1.6-1-3-3-1 .6-3.1L1 12l2.1-2.4-.6-3.1 3-1 1-3 3.1.6L12 1.5z"
          fill="url(#discountBadgeGradient)"
        />
        <path
          d="M9 15l6-6"
          stroke="#fff"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
        <path
          d="M9.5 9.5h.01M14.5 14.5h.01"
          stroke="#fff"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

// "Add a 2nd printed for 50% OFF" / "Add a 2nd digital for 50% OFF" / "Add a 2nd printed or digital for 50% OFF".
// The discount only pairs books of the same kind, so the nudge names the kind.
function upsellCopy(kinds: BagTotals["upsellKinds"]): string {
  if (kinds.length === 0) return `Get ${BAG_DISCOUNT_PERCENT}% off your 2nd book and up`;
  return `Add a 2nd ${kinds.join(" or ")} for ${BAG_DISCOUNT_PERCENT}% OFF`;
}

// "printed" / "digital" / "printed and digital" — which books the discount is applying to.
function kindLabel(kinds: BagTotals["discountKinds"]): string {
  return kinds.length ? kinds.join(" and ") : "";
}

// Progress toward the discount: 1 book of a kind = halfway, a 2nd of the same kind = full.
function discountProgress(totals: BagTotals): number {
  if (totals.discountActive) return 1;
  return totals.upsellKinds.length > 0 ? 0.5 : 0;
}

export default function BagPromoBar({ totals }: { totals: BagTotals }) {
  const [showSaving, setShowSaving] = useState(totals.discountActive);
  const [barProgress, setBarProgress] = useState(discountProgress(totals));
  const prevActive = useRef(totals.discountActive);

  useEffect(() => {
    const wasActive = prevActive.current;
    prevActive.current = totals.discountActive;

    setBarProgress(discountProgress(totals));
    if (!totals.discountActive) {
      setShowSaving(false);
      return;
    }
    // Just turned on: fill the bar first, then swap to the "You're saving" message. Already on
    // (e.g. a 2nd cart update cancelled that timer) → swap right away, so it can't get stuck.
    const t = setTimeout(() => setShowSaving(true), wasActive ? 0 : 600);
    return () => clearTimeout(t);
  }, [totals.discountActive, totals.upsellKinds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      layout
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      className="bg-[#f7f7f7] px-5 py-4 overflow-hidden"
    >
      <AnimatePresence mode="wait" initial={false}>
        {showSaving ? (
          <motion.div
            key="saving"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex items-center gap-2"
          >
            <motion.span
              initial={{ scale: 0.6, rotate: -15 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 18, delay: 0.1 }}
            >
              <DiscountBadge />
            </motion.span>
            <div>
              <p className="text-[16px] text-[#333]">
                <span className="font-semibold text-[#F52F81]">
                  You&apos;re saving <AnimatedSavings amount={totals.totalSavings} />
                </span>
              </p>
              <p className="text-[13px] text-[#828282]">
                {BAG_DISCOUNT_PERCENT}% off your 2nd {kindLabel(totals.discountKinds)} book
                {totals.upsellKinds.length > 0
                  ? ` · add another ${totals.upsellKinds.join(" or ")} book to save more`
                  : ""}
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="upsell"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="flex items-center gap-2">
              <DiscountBadge />
              <p className="text-[16px] text-[#333]">{upsellCopy(totals.upsellKinds)}</p>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-[5px] flex-1 overflow-hidden rounded-full bg-[#e0e0e0]">
                <motion.div
                  className="h-full rounded-full bg-[#333]"
                  initial={false}
                  animate={{ width: `${barProgress * 100}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              <span className="text-[12px] text-[#828282]" style={{ fontWeight: 500 }}>
                {BAG_DISCOUNT_PERCENT}% off
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
